import React, { createContext, useCallback, useContext, useMemo } from "react";
import type { IInteractionRpc } from "@pulse-ui/codegen/generated_ts/managed_algo";
import { useEndpointFetch } from "../hooks/useEndpointFetch";

export type CallUseEndpointResult =
  | { success: true; data: Record<string, unknown> }
  | { success: false; missingPaths: string[] };

export type CallUseEndpoint = (
  endpointRpcName: string,
  formData: Record<string, unknown>
) => Promise<CallUseEndpointResult>;

const UseEndpointActionsContext = createContext<{
  callUseEndpoint: CallUseEndpoint | null;
}>({ callUseEndpoint: null });

const UseEndpointValidationContext = createContext<{
  missingPaths: string[];
  missingPathsSet: Set<string> | null;
  endpointName: string | null;
}>({ missingPaths: [], missingPathsSet: null, endpointName: null });

export const useUseEndpointActions = () =>
  useContext(UseEndpointActionsContext);
export const useUseEndpointValidation = () =>
  useContext(UseEndpointValidationContext);

export interface UseEndpointProviderProps {
  interaction: IInteractionRpc;
  submitRpc: (args: { data: unknown }) => unknown;
  showError: (message: string) => void;
  children: React.ReactNode;
}

/**
 * Provides use_endpoint callback and validation state to RPC form controls.
 * Wrap the form (e.g. RpcForm) with this provider so controls can fetch
 * values from referenced RPCs and show missing-required-field errors.
 *
 * **Note:** This context now uses the consolidated `useEndpointFetch` hook internally.
 * New code should use `useEndpointFetch` directly instead of this context.
 */
export function UseEndpointProvider({
  interaction,
  submitRpc,
  showError,
  children,
}: UseEndpointProviderProps) {
  // Use the new consolidated hook
  const { fetchEndpoint, fetchState } = useEndpointFetch({
    interaction,
    submitRpc,
    showError,
  });

  // Adapt the new hook's interface to the old context interface
  const callUseEndpoint = useCallback<CallUseEndpoint>(
    async (endpointRpcName, formData) => {
      // The new hook's fetchEndpoint expects a propertyName parameter,
      // but the old context API doesn't specify which property to extract.
      // We'll return the full data object and let the caller extract what they need.
      const result = await fetchEndpoint(endpointRpcName, formData, "");
      return result;
    },
    [fetchEndpoint]
  );

  const missingPathsSet = useMemo(
    () =>
      fetchState.missingPaths.length > 0
        ? new Set(fetchState.missingPaths)
        : null,
    [fetchState.missingPaths]
  );

  const actionsValue = useMemo(
    () => ({ callUseEndpoint }),
    [callUseEndpoint]
  );
  const validationValue = useMemo(
    () => ({
      missingPaths: fetchState.missingPaths,
      missingPathsSet,
      endpointName: fetchState.endpointName,
    }),
    [fetchState.missingPaths, missingPathsSet, fetchState.endpointName]
  );

  return (
    <UseEndpointActionsContext.Provider value={actionsValue}>
      <UseEndpointValidationContext.Provider value={validationValue}>
        {children}
      </UseEndpointValidationContext.Provider>
    </UseEndpointActionsContext.Provider>
  );
}
